import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {SpotlightData} from "types/Spotlight";
import SpotlightForm from '../Form';

function MutateSpotlight() {
    const {id} = useParams()
    const {data: spotlight, isLoading} = useQuery(
        ['spotlights', {id}],
        () => ngage.spotlights.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: SpotlightData) => ngage.spotlights.store(data))
    const update = useMutation((data: SpotlightData) => ngage.spotlights.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: SpotlightData, {setSubmitting}: FormikHelpers<SpotlightData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('spotlights')

        setSubmitting(false)
        navigate('/spotlights')
    }

    // Handle store mutation
    async function handleStore(values: SpotlightData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Spotlight Created", `${values.title} has been created`)
        } catch (error) {
            notify("error", "Spotlight Not Created", `${values.title} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: SpotlightData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Spotlight Updated", `${values.title} has been updated`)
            await invalidate(['spotlights', {id}])
        } catch (error) {
            notify("error", "Spotlight Not Updated", `${values.title} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'App Content': '/', 'Featured': '/', 'Spotlights': '/spotlights', 'New Spotlight': '/spotlights/new'}
    const editBreadcrumbs = {'App Content': '/', 'Featured': '/', 'Spotlights': '/spotlights', 'Edit Spotlight': `/spotlights/${id}/edit`}

    return (
        <Page
          title={id ? "Edit Spotlight" : "New Spotlight"}
          breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
          isLoading={isLoading}
        >
            <SpotlightForm spotlight={spotlight} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateSpotlight;